// src/components/business/BusinessBrandingTab.tsx
//
// Sub-aba "Branding" da aba Marketing (DEM-2123 · Marketing F4a).
// Dropzone + clique pra selecionar arquivo; lista de arquivos com ações
// completas: download, excluir, renomear (edita filename) e editar descrição.
// Os arquivos vivem no path canônico do Storage
// (raios-tenant-assets/{tenant_id}/businesses/{business_id}/marketing/branding/)
// e a listagem (metadado + descrição) é o que um agente lê antes de baixar.
//
// C6: dados LAZY — busca os próprios assets quando o componente monta.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type BusinessAsset } from '@/lib/api';
import { BizModal } from './BizModal';
import { WfButton, WfEmpty } from '@/components/wf';
import { Download, FileText, Pencil, Trash2, Upload } from 'lucide-react';

interface BusinessBrandingTabProps {
  businessId: string;
}

/** Formata bytes em unidade legível (KB/MB). */
function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function BusinessBrandingTab({ businessId }: BusinessBrandingTabProps) {
  const [assets, setAssets] = useState<BusinessAsset[] | null>(null);
  const [listMsg, setListMsg] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  // Modal editar (renomear + descrição)
  const [editAsset, setEditAsset] = useState<BusinessAsset | null>(null);
  const [editFilename, setEditFilename] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editMsg, setEditMsg] = useState('');

  // LAZY: busca quando a sub-aba monta.
  useEffect(() => {
    let alive = true;
    api.listBusinessAssets(businessId, 'branding')
      .then((a) => { if (alive) setAssets(a); })
      .catch((err) => {
        if (alive) {
          setAssets([]);
          setListMsg(err instanceof Error ? err.message : 'Erro ao carregar arquivos');
        }
      });
    return () => { alive = false; };
  }, [businessId]);

  const doUpload = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    setUploadMsg('');
    try {
      for (const file of list) {
        const asset = await api.uploadBusinessAsset(businessId, file, { kind: 'branding' });
        setAssets((prev) => [asset, ...(prev ?? [])]);
      }
    } catch (err) {
      setUploadMsg(err instanceof Error ? err.message : 'Erro ao subir arquivo');
    } finally {
      setUploading(false);
    }
  }, [businessId]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) void doUpload(e.dataTransfer.files);
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) void doUpload(e.target.files);
    e.target.value = ''; // permite re-selecionar o mesmo arquivo
  };

  const handleDownload = async (asset: BusinessAsset) => {
    setListMsg('');
    try {
      const { signed_url } = await api.getBusinessAssetDownloadUrl(businessId, asset.id);
      window.open(signed_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setListMsg(err instanceof Error ? err.message : 'Erro ao baixar arquivo');
    }
  };

  const handleDelete = async (assetId: string) => {
    setListMsg('');
    try {
      await api.deleteBusinessAsset(businessId, assetId);
      setAssets((prev) => (prev ?? []).filter((a) => a.id !== assetId));
    } catch (err) {
      setListMsg(err instanceof Error ? err.message : 'Erro ao excluir arquivo');
    }
  };

  const handleOpenEdit = (a: BusinessAsset) => {
    setEditAsset(a);
    setEditFilename(a.filename);
    setEditDescription(a.description ?? '');
    setEditMsg('');
  };

  const handleSaveEdit = async () => {
    if (!editAsset) return;
    setEditLoading(true);
    setEditMsg('');
    try {
      let updated = editAsset;
      const payload: Partial<{ filename: string; description: string | null }> = {};
      if (editFilename.trim() && editFilename.trim() !== editAsset.filename) {
        payload.filename = editFilename.trim();
      }
      if ((editDescription ?? '') !== (editAsset.description ?? '')) {
        payload.description = editDescription.trim() ? editDescription.trim() : null;
      }
      if (Object.keys(payload).length > 0) {
        updated = await api.updateBusinessAsset(businessId, editAsset.id, payload);
        setAssets((prev) => (prev ?? []).map((a) => (a.id === updated.id ? updated : a)));
      }
      setEditAsset(null);
    } catch (err) {
      setEditMsg(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="wf-bizpanel">
      {/* Header */}
      <div className="wf-bizpanel-h">
        <span>
          Branding <span className="wf-bpcount">{assets?.length ?? '…'}</span>
        </span>
      </div>

      {/* Dropzone (drag&drop + clique) */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className="wf-dropzone"
        style={{
          border: `2px dashed ${dragOver ? 'var(--wf-accent-text)' : 'var(--wf-border)'}`,
          borderRadius: 12,
          padding: '28px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'var(--wf-accent-bg, rgba(0,0,0,0.02))' : 'transparent',
          transition: 'border-color .15s, background .15s',
          marginBottom: 16,
        }}
        aria-label="Enviar arquivo de branding"
      >
        <Upload className="ic" aria-hidden="true" style={{ width: 24, height: 24, opacity: 0.6, display: 'block', margin: '0 auto 8px' }} />
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {uploading ? 'Enviando…' : 'Arraste arquivos aqui ou clique para selecionar'}
        </div>
        <div className="text-xs" style={{ color: 'var(--wf-muted)', marginTop: 4 }}>
          PDF, MD, TXT, HTML, DOC/DOCX, XLS/XLSX/CSV e imagens (até 1 GB)
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={handleFilePick}
          accept=".pdf,.md,.txt,.html,.doc,.docx,.xls,.xlsx,.csv,image/*"
        />
      </div>
      {uploadMsg && <p className="text-xs mb-2" style={{ color: 'var(--wf-danger-text)' }}>{uploadMsg}</p>}
      {listMsg && <p className="text-xs mb-2" style={{ color: 'var(--wf-danger-text)' }}>{listMsg}</p>}

      {/* Lista de arquivos */}
      {assets === null ? (
        <div className="wf-repolist" aria-busy="true">
          {[1, 2].map((i) => (
            <div key={i} className="wf-repocard" style={{ minHeight: 56 }} />
          ))}
        </div>
      ) : assets.length === 0 ? (
        <WfEmpty icon={<FileText />}>
          Nenhum arquivo de branding — envie logotipos, guias de marca e outros
          materiais. Os agentes encontram esses arquivos deterministicamente pelo
          business.
        </WfEmpty>
      ) : (
        <div className="wf-repolist">
          {assets.map((a) => (
            <div key={a.id} className="wf-repocard">
              <div className="rico">
                <FileText className="ic" aria-hidden="true" />
              </div>
              <div className="rinfo">
                <div className="rname">{a.filename}</div>
                <div className="rurl">
                  {a.description
                    ? a.description
                    : <span style={{ color: 'var(--wf-muted)' }}>Sem descrição</span>}
                </div>
              </div>
              <div className="wf-repobr">
                <span className="wf-rtype">{a.content_type}</span>
                <span className="wf-br">{formatBytes(a.size_bytes)}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
                <button
                  type="button"
                  className="wf-iconbtn"
                  aria-label="Baixar arquivo"
                  title="Baixar arquivo"
                  onClick={() => void handleDownload(a)}
                >
                  <Download aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="wf-iconbtn"
                  aria-label="Renomear ou editar descrição"
                  title="Renomear / editar descrição"
                  onClick={() => handleOpenEdit(a)}
                >
                  <Pencil aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="wf-iconbtn wf-iconbtn--no"
                  aria-label="Excluir arquivo"
                  title="Excluir arquivo"
                  onClick={() => void handleDelete(a.id)}
                >
                  <Trash2 aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: renomear + editar descrição */}
      <BizModal
        open={editAsset !== null}
        onClose={() => setEditAsset(null)}
        title={`Editar arquivo${editAsset ? ` · ${editAsset.filename}` : ''}`}
        icon={<Pencil className="ic" aria-hidden="true" />}
        footer={
          <>
            <WfButton variant="ghost" onClick={() => setEditAsset(null)}>Cancelar</WfButton>
            <WfButton variant="primary" disabled={editLoading} onClick={handleSaveEdit}>
              {editLoading ? 'Salvando…' : 'Salvar'}
            </WfButton>
          </>
        }
      >
        <div className="wf-field">
          <label className="wf-label" htmlFor="asset-filename">Nome do arquivo</label>
          <input
            id="asset-filename"
            className="wf-input"
            value={editFilename}
            onChange={(e) => setEditFilename(e.target.value)}
            placeholder="logo-oficial.png"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label" htmlFor="asset-description">Descrição</label>
          <textarea
            id="asset-description"
            className="wf-input"
            rows={3}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Contexto que o agente lê antes de baixar (ex: logo principal em fundo claro)"
          />
        </div>
        {editMsg && <p className="text-xs" style={{ color: 'var(--wf-danger-text)' }}>{editMsg}</p>}
      </BizModal>
    </div>
  );
}
